import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, Briefcase, Calendar, CheckCircle, GraduationCap, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHero from '../../components/UI/PageHero';
import ProfileAvatar from '../../components/UI/ProfileAvatar';
import {
  displayEducationStatus,
  displayProfileValue,
  displayWorkingStatus,
  parseUserInfoJson,
} from '../../utils/userInfoJson';
import { contentApi } from '../../services/contentApi';
import { focusPageTop } from '../../utils/focusPageTop';
import { ENROLLMENTS_CHANGED, getCachedEnrollments } from '../../services/enrollmentCache';
import { academyAdminApi, interviewApi } from '../../services/api';
import { CourseEnrollment, CourseProgress, EventEnrollment, ProjectEnrollment } from '../../types/content';
import { courseTopicPath } from '../../utils/coursePaths';
import { InterviewRequestItem } from '../../types';
import { canWriteEvents, canWriteProjects, isInterviewer, isMentor, isOwner } from '../../utils/roles';
import CourseFee from '../../components/UI/CourseFee';
import { getUserProfileId } from '../../utils/userProfile';
import { DashboardTab, isDashboardTab } from '../../utils/dashboardTabs';
import { formatDate } from '../../utils/formatDate';
import { useMyContributorApplications } from '../../hooks/useMyContributorApplications';
import { contributorStatusLabel, isApprovedContributor } from '../../utils/contributorApplications';
import CourseCoverImage from '../../components/content/CourseCoverImage';
import { COURSE_IMAGES } from '../../config/courseImages';
type LearningStatus = 'start' | 'progress' | 'completed';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const statusLabel = (status: LearningStatus) => {
  if (status === 'completed') return 'Completed';
  if (status === 'progress') return 'In progress';
  return 'Start learning';
};

const statusBadgeClass = 'inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-800 ring-1 ring-indigo-200';

const courseStatus = (enrollment: CourseEnrollment, progress?: CourseProgress | null): LearningStatus => {
  if (enrollment.isCourseCompleted || progress?.isCourseCompleted) return 'completed';
  if ((progress?.completedTopicCount || enrollment.completedTopicCount || 0) > 0
    || (progress?.completedLabCount || enrollment.completedLabCount || 0) > 0) {
    return 'progress';
  }
  return 'start';
};

const Dashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { mentor: mentorApplication, interviewer: interviewerApplication } = useMyContributorApplications();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: DashboardTab = isDashboardTab(requestedTab) ? requestedTab : 'courses';
  const [courseEnrollments, setCourseEnrollments] = useState<CourseEnrollment[]>([]);
  const [progressByCourse, setProgressByCourse] = useState<Record<number, CourseProgress>>({});
  const [interviewRequests, setInterviewRequests] = useState<InterviewRequestItem[]>([]);
  const [assignedInterviews, setAssignedInterviews] = useState<InterviewRequestItem[]>([]);
  const [schoolPrograms, setSchoolPrograms] = useState<{ courseId: number; courseTitle?: string; status: string }[]>([]);
  const [eventEnrollments, setEventEnrollments] = useState<EventEnrollment[]>([]);
  const [projectEnrollments, setProjectEnrollments] = useState<ProjectEnrollment[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const extendedInfo = parseUserInfoJson(user?.userInfoJson);
  const memberSince = user?.dateCreated
    ? new Date(user.dateCreated).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
  const learningCourses = useMemo(
    () => courseEnrollments.filter((item) => !/^cancelled$/i.test(item.status || '')),
    [courseEnrollments]
  );

  useEffect(() => {
    const applyCached = () => {
      const courses = getCachedEnrollments<CourseEnrollment>('courses');
      const events = getCachedEnrollments<EventEnrollment>('events');
      const projects = getCachedEnrollments<ProjectEnrollment>('projects');
      if (courses) setCourseEnrollments(courses);
      if (events) setEventEnrollments(events);
      if (projects) setProjectEnrollments(projects);
    };
    applyCached();
    const hasCache = Boolean(
      getCachedEnrollments('courses')
      || getCachedEnrollments('events')
      || getCachedEnrollments('projects')
    );
    if (!hasCache) setCatalogLoading(true);
    window.addEventListener(ENROLLMENTS_CHANGED, applyCached);
    const profileId = getUserProfileId(user);
    Promise.all([
      isAuthenticated ? contentApi.refreshCourseEnrollments().catch(() => ({ data: { enrollments: [] as CourseEnrollment[] } })) : Promise.resolve({ data: { enrollments: [] as CourseEnrollment[] } }),
      isAuthenticated ? contentApi.refreshEventEnrollments().catch(() => ({ data: { enrollments: [] as EventEnrollment[] } })) : Promise.resolve({ data: { enrollments: [] as EventEnrollment[] } }),
      isAuthenticated ? contentApi.refreshProjectEnrollments().catch(() => ({ data: { enrollments: [] as ProjectEnrollment[] } })) : Promise.resolve({ data: { enrollments: [] as ProjectEnrollment[] } }),
      isAuthenticated && profileId ? academyAdminApi.enrollmentsByUser(profileId).catch(() => ({ data: { enrolledCourses: [] } })) : Promise.resolve({ data: { enrolledCourses: [] } }),
      isAuthenticated && profileId
        ? interviewApi.getByUser(profileId).catch(() => ({ data: { interviewRequests: [] as InterviewRequestItem[] } }))
        : Promise.resolve({ data: { interviewRequests: [] as InterviewRequestItem[] } }),
      isAuthenticated
        ? interviewApi.getAssigned().catch(() => ({ data: { interviewRequests: [] as InterviewRequestItem[] } }))
        : Promise.resolve({ data: { interviewRequests: [] as InterviewRequestItem[] } }),
    ])
      .then(async ([enrollRes, eventRes, projectRes, schoolRes, interviewRes, assignedRes]) => {
        const enrollments = enrollRes.data.enrollments || [];
        setCourseEnrollments(enrollments);
        setEventEnrollments(eventRes.data.enrollments || []);
        setProjectEnrollments(projectRes.data.enrollments || []);
        setSchoolPrograms(schoolRes.data.enrolledCourses || []);
        setInterviewRequests(
          [...(interviewRes.data.interviewRequests || [])].sort(
            (a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime()
          )
        );
        setAssignedInterviews(assignedRes.data.interviewRequests || []);
        const active = enrollments.filter((item) => !/^cancelled$/i.test(item.status || ''));
        const progressEntries = await Promise.all(active.map(async (item) => {
          try {
            const response = await contentApi.getCourseProgress(item.courseId);
            return [item.courseId, response.data.progress] as const;
          } catch {
            return [item.courseId, undefined] as const;
          }
        }));
        const map: Record<number, CourseProgress> = {};
        progressEntries.forEach(([courseId, progress]) => {
          if (progress) map[courseId] = progress;
        });
        setProgressByCourse(map);
      })
      .catch(() => undefined)
      .finally(() => setCatalogLoading(false));
    return () => window.removeEventListener(ENROLLMENTS_CHANGED, applyCached);
  }, [isAuthenticated, user]);

  const tabs: { id: DashboardTab; label: string; icon: typeof BookOpen; count: number }[] = [
    { id: 'courses', label: 'Courses', icon: BookOpen, count: learningCourses.length },
    { id: 'projects', label: 'Projects', icon: Briefcase, count: projectEnrollments.length },
    { id: 'academy', label: 'IDC Academy', icon: GraduationCap, count: schoolPrograms.length },
    { id: 'interviews', label: 'Interviews', icon: MessageSquare, count: interviewRequests.length + assignedInterviews.length },
    { id: 'events', label: 'Events', icon: Calendar, count: eventEnrollments.length },
  ];
  const selectTab = (tab: DashboardTab) => {
    setSearchParams(tab === 'courses' ? {} : { tab }, { replace: true });
    focusPageTop();
  };

  return (
    <div>
      <PageHero
        tone="account"
        title={`Welcome, ${user?.username || user?.email?.split('@')[0]}`}
        subtitle="Continue building your skills and opportunities. Access your Courses, Projects, IDC Academy programs, Interviews, and Events from your dashboard."
      >
        <Link to="/" className="inline-flex text-sm font-medium text-white/90 hover:text-white">
          ← Back to home
        </Link>
      </PageHero>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="md:col-span-1">
            <Card className="h-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <ProfileAvatar
                    src={user?.displayImageUrl || user?.profileImageUrl}
                    alt={user?.username || user?.email || 'Profile'}
                    className="h-14 w-14 rounded-full object-cover bg-gray-100 ring-2 ring-indigo-100"
                  />
                  <div>
                    <h2 className="text-xl font-semibold">Account</h2>
                    {memberSince && <p className="text-sm text-gray-500">Member since {memberSince}</p>}
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Username</p>
                    <p className="font-medium">{displayProfileValue(user?.username)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Mobile</p>
                    <p className="font-medium">{displayProfileValue(user?.mobile)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email Verification</p>
                    <p className={`font-medium ${user?.isEmailVerified ? 'text-success-600' : 'text-warning-600'}`}>
                      {user?.isEmailVerified ? 'Verified' : 'Not Verified'}
                    </p>
                  </div>
                  {isOwner(user) && (
                    <div>
                      <p className="text-sm text-gray-500">Role</p>
                      <p className="font-medium text-primary-700">Owner · Super admin</p>
                    </div>
                  )}
                  {(isMentor(user) || isApprovedContributor(mentorApplication)) && (
                    <div>
                      <p className="text-sm text-gray-500">Mentor</p>
                      <p className="font-medium text-success-600">Approved</p>
                    </div>
                  )}
                  {(isInterviewer(user) || isApprovedContributor(interviewerApplication)) && (
                    <div>
                      <p className="text-sm text-gray-500">Interviewer</p>
                      <p className="font-medium text-success-600">Approved</p>
                    </div>
                  )}
                  {(mentorApplication?.hasApplication || interviewerApplication?.hasApplication) && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Application status</p>
                      {mentorApplication?.hasApplication && (
                        <div className="mb-2">
                          <p className="text-sm text-gray-500">Mentor</p>
                          <p className="font-medium">{contributorStatusLabel(mentorApplication)}</p>
                          {mentorApplication.displayStatus === 'Rejected' && mentorApplication.rejectionReason ? (
                            <p className="text-xs text-gray-500 mt-0.5">{mentorApplication.rejectionReason}</p>
                          ) : null}
                        </div>
                      )}
                      {interviewerApplication?.hasApplication && (
                        <div>
                          <p className="text-sm text-gray-500">Interviewer</p>
                          <p className="font-medium">{contributorStatusLabel(interviewerApplication)}</p>
                          {interviewerApplication.displayStatus === 'Rejected' && interviewerApplication.rejectionReason ? (
                            <p className="text-xs text-gray-500 mt-0.5">{interviewerApplication.rejectionReason}</p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Education</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">College</p>
                        <p className="font-medium">{displayProfileValue(extendedInfo.college)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Degree</p>
                        <p className="font-medium">{displayProfileValue(extendedInfo.degree)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Completed or pursuing</p>
                        <p className="font-medium">{displayEducationStatus(extendedInfo.educationStatus)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Employment</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Currently working</p>
                        <p className="font-medium">{displayWorkingStatus(extendedInfo.isWorking)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Company</p>
                        <p className="font-medium">{displayProfileValue(extendedInfo.companyName)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Role</p>
                        <p className="font-medium">{displayProfileValue(extendedInfo.role)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Location & communication</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium">{displayProfileValue(extendedInfo.location)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Address for communication</p>
                        <p className="font-medium">{displayProfileValue(extendedInfo.address)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Link to="/profile/edit">
                    <Button variant="outline" fullWidth>Edit Profile</Button>
                  </Link>
                  {isOwner(user) && (
                    <Link to="/admin/dashboard">
                      <Button fullWidth>Open Admin Dashboard</Button>
                    </Link>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="md:col-span-2">
            <Card className="h-full">
              <div className="border-b border-gray-200 px-4 sm:px-6 overflow-x-auto">
                <nav className="flex gap-1 min-w-max" role="tablist" aria-label="Dashboard sections">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const selected = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => selectTab(tab.id)}
                        className={`relative inline-flex items-center gap-2 rounded-t-lg px-3 py-3.5 text-sm font-semibold transition-colors ${
                          selected ? 'text-primary-700 bg-primary-50/80' : 'text-gray-500 hover:text-gray-800 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {tab.label}
                        <span className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
                          selected ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tab.count}
                        </span>
                        {selected && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-600" />}
                      </button>
                    );
                  })}
                </nav>
              </div>

              <div className="p-4 sm:p-6">
                {catalogLoading ? (
                  <div className="flex justify-center items-center min-h-[120px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" />
                  </div>
                ) : null}

                {activeTab === 'academy' && !catalogLoading && (
                  <div>
                    {schoolPrograms.length === 0 ? (
                      <div className="text-center text-gray-600 flex flex-col items-center gap-4">
                        <div>You are not enrolled in an IDC Academy program yet.</div>
                        <Link to="/programs"><Button variant="primary">Explore IDC Academy</Button></Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {schoolPrograms.map((item) => (
                          <div key={`${item.courseId}-${item.status}`} className="rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                              <p className="font-semibold text-gray-900">{item.courseTitle || `Program ${item.courseId}`}</p>
                              <p className="mt-1"><span className={statusBadgeClass}>{item.status}</span></p>
                            </div>
                            <Link to={`/programs/${item.courseId}`}><Button size="sm">Open program</Button></Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'courses' && !catalogLoading && (
                  <div>
                    {learningCourses.length === 0 ? (
                      <div className="text-center text-gray-600 flex flex-col items-center gap-4">
                        <div>You are not enrolled in a course yet.</div>
                        <Link to="/courses"><Button variant="primary">Browse courses</Button></Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {learningCourses.map((item) => {
                          const progress = progressByCourse[item.courseId];
                          const status = courseStatus(item, progress);
                          const nextIncomplete = progress?.modules
                            .flatMap((module) => module.topics.map((topic) => ({ ...topic, moduleId: module.id })))
                            .find((topic) => !topic.isCompleted);
                          const nextHref = nextIncomplete
                            ? courseTopicPath(item.courseSlug, nextIncomplete.slug, nextIncomplete.moduleId)
                            : item.firstTopicSlug
                              ? courseTopicPath(item.courseSlug, item.firstTopicSlug)
                              : `/courses/${item.courseSlug}`;
                          return (
                            <div key={item.id} className="rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div>
                                <Link to={`/courses/${item.courseSlug}`} className="font-semibold text-gray-900 hover:text-primary-700">{item.courseTitle}</Link>
                                <p className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-2">
                                  {status === 'completed' && <CheckCircle className="h-4 w-4 text-success-500" />}
                                  <span className={statusBadgeClass}>{statusLabel(status)}</span>
                                  {progress ? ` · ${progress.completedTopicCount}/${progress.topicCount} topics` : null}
                                  {' · '}<CourseFee fee={item.price} discount={item.discountPercent} />
                                </p>
                              </div>
                              <Link to={nextHref}>
                                <Button size="sm">{status === 'completed' ? 'Review' : 'Continue learning'}</Button>
                              </Link>
                            </div>
                          );
                        })}
                        <div className="pt-2">
                          <Link to="/courses"><Button variant="outline" size="sm">Browse courses</Button></Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'interviews' && !catalogLoading && (
                  <div className="space-y-8">
                    {assignedInterviews.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Sessions to conduct</h3>
                        <div className="space-y-3">
                          {assignedInterviews.map((item) => (
                            <div key={`assigned-${item.id}`} className="rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div>
                                <p className="font-semibold text-gray-900">{item.otherTechnology || item.technology} interview</p>
                                <p className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-x-2">
                                  {item.status === 'Completed' && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                                  <span className={statusBadgeClass}>{item.status}</span>
                                  {item.candidateName ? <span>· {item.candidateName}</span> : null}
                                  {item.scheduledDate ? <span>· {formatDate(item.scheduledDate)}{item.scheduledTime ? ` ${item.scheduledTime}` : ''}</span> : null}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {item.meetingLink && item.status !== 'Completed' ? (
                                  <a href={item.meetingLink} target="_blank" rel="noreferrer">
                                    <Button size="sm">Join meeting</Button>
                                  </a>
                                ) : null}
                                <Link to={`/interviews/${item.id}`}>
                                  <Button size="sm">{item.status === 'Completed' ? 'View interview' : 'Review feedback'}</Button>
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Interviews I booked</h3>
                    {interviewRequests.length === 0 ? (
                      <div className="text-center text-gray-600 flex flex-col items-center gap-4">
                        <div>You have not booked a technical interview yet.</div>
                        <Link to="/interviews"><Button variant="primary">Book an interview</Button></Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {interviewRequests.map((item) => {
                          const technology = item.otherTechnology || item.technology;
                          const scheduled = item.scheduledDate
                            ? `${formatDate(item.scheduledDate)}${item.scheduledTime ? ` · ${item.scheduledTime}` : ''}`
                            : null;
                          return (
                            <div key={item.id} className="rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {item.contentCourseTitle
                                    ? `${item.contentCourseTitle}${item.courseInterviewSession ? ` · Interview ${item.courseInterviewSession}` : ''}`
                                    : `${technology} interview`}
                                </p>
                                <p className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                                  {item.status === 'Completed' && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                                  <span className={statusBadgeClass}>{item.status}</span>
                                  <span>· {item.contentCourseTitle
                                    ? 'Included with course'
                                    : item.price <= 0
                                      ? '₹0'
                                      : `₹${Number(item.price).toLocaleString('en-IN')}`}</span>
                                  {item.role ? <span>· {item.role}</span> : null}
                                  {scheduled ? <span>· {scheduled}</span> : item.requestDate ? <span>· Requested {formatDate(item.requestDate)}</span> : null}
                                  {item.interviewerName ? <span>· {item.interviewerName}</span> : null}
                                </p>
                                {item.rejectionReason ? (
                                  <p className="text-sm text-gray-500 mt-1">{item.rejectionReason}</p>
                                ) : null}
                                {!item.feedback && item.status !== 'Cancelled' ? (
                                  <p className="text-sm text-amber-700 mt-2">Feedback pending</p>
                                ) : null}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {item.meetingLink && item.status !== 'Completed' ? (
                                  <a href={item.meetingLink} target="_blank" rel="noreferrer">
                                    <Button size="sm">Join meeting</Button>
                                  </a>
                                ) : null}
                                <Link to={`/interviews/${item.id}`}>
                                  <Button size="sm" variant="outline">Review feedback</Button>
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                        <div className="pt-2">
                          <Link to="/interviews"><Button variant="outline" size="sm">Book another interview</Button></Link>
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                )}

                {activeTab === 'events' && !catalogLoading && (
                  <CatalogList
                    empty="You are not enrolled in an event yet."
                    exploreTo="/events"
                    exploreLabel="Browse events"
                    extra={canWriteEvents(user) ? <Link to="/my-events"><Button variant="outline" size="sm">Manage events</Button></Link> : null}
                    items={eventEnrollments.map((item) => ({
                      key: item.id,
                      to: `/events/${item.eventSlug}`,
                      title: item.eventTitle,
                      detail: `${item.mode}`,
                      status: item.status,
                      meta: item.meetingLink ? 'Link available' : null,
                    }))}
                  />
                )}

                {activeTab === 'projects' && !catalogLoading && (
                  <CatalogList
                    empty="You have not started a self-paced project yet."
                    exploreTo="/projects"
                    exploreLabel="Browse projects"
                    extra={canWriteProjects(user) ? <Link to="/my-projects"><Button variant="outline" size="sm">Manage my projects</Button></Link> : null}
                    items={projectEnrollments.map((item) => ({
                      key: item.id,
                      to: `/projects/${item.projectSlug}`,
                      title: item.projectTitle,
                      detail: `Self-paced · ${item.company || 'Project'}`,
                      status: item.status,
                      imageUrl: item.coverImageUrl,
                    }))}
                  />
                )}

              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const CatalogList: React.FC<{
  items: { key: number; to: string; title: string; detail?: string | null; status?: string | null; meta?: string | null; imageUrl?: string | null }[];
  empty: string;
  exploreTo: string;
  exploreLabel: string;
  extra?: React.ReactNode;
}> = ({ items, empty, exploreTo, exploreLabel, extra }) => (
  <div>
    <div className="flex justify-end mb-4">{extra}</div>
    {items.length === 0 ? (
      <div className="text-center text-gray-600 flex flex-col items-center gap-4">
        <div>{empty}</div>
        <Link to={exploreTo}><Button variant="primary">{exploreLabel}</Button></Link>
      </div>
    ) : (
      <div className="space-y-3">
        {items.map((item) => (
          <Link key={item.key} to={item.to} className="flex gap-3 rounded-xl border border-gray-100 p-4 hover:border-primary-200">
            {item.imageUrl !== undefined && (
              <CourseCoverImage
                src={item.imageUrl}
                fallbackSrc={COURSE_IMAGES.projectsHero}
                className="h-16 w-24 rounded-lg object-cover shrink-0"
              />
            )}
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900">{item.title}</h3>
              <p className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-2">
                {item.detail}
                {item.status ? <span className={statusBadgeClass}>{item.status}</span> : null}
                {item.meta ? <span>· {item.meta}</span> : null}
              </p>
            </div>
          </Link>
        ))}
        <div className="pt-2">
          <Link to={exploreTo}><Button variant="outline" size="sm">{exploreLabel}</Button></Link>
        </div>
      </div>
    )}
  </div>
);

export default Dashboard;
